<template>
    <SectionBase v-bind="$attrs"  :section="section" :style="[colors]">
        <div ref="processBar"
             :class="['sticky-header', {sticked : stuck}, {'sticked-hidden' : hideStuckVersion}, {'show-dd' : showMobileProcessSteps}, {dark:portfolioIsDark}]"
             id="sticky-header"
             :data-start="setStartSection"
             :data-end="setEndSection"
        >
            <div class="process-mobile-display">
                <div class="process-step-display process-step checked"></div>
                <div class="process-dropdown-toggle" @click="toggleProcessSteps()">
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.60958 0.687671C8.78209 0.47204 9.09674 0.437079 9.31237 0.609584C9.528 0.782089 9.56296 1.09674 9.39045 1.31237L5.39045 5.31237C5.19029 5.56257 4.80975 5.56257 4.60958 5.31237L0.609584 1.31237C0.437079 1.09674 0.47204 0.782089 0.687671 0.609584C0.903302 0.437079 1.21795 0.47204 1.39045 0.687671L5.00002 4.19963L8.60958 0.687671Z" fill="var(--section-h2-text-color, #ffffff)"/>
                    </svg>
                </div>
            </div>
            <div ref="processSteps" :class="['process-steps', processItems.length > 4 ? 'long' : '']">
                <template v-for="(step, index) in processItems">
                    <AnimationWrapper :delay-animation="index ? index * 0.1 : 0">
                        <template #animationContent>
                            <div class="process-step" :key="`step-${step.sectionIndex}`">
                                <div class="process-step-container">
                                    <a :data-href="step.sectionIndex ? '#section-' + step.sectionIndex :  '#section-' + section.index"
                                       :href="step.sectionIndex ? '#section-' + step.sectionIndex :  '#section-' + section.index"
                                       @click="(evt) => scrollToItem(evt, step.sectionIndex ? 'section-' + step.sectionIndex :  'section-' + section.index)"
                                       class="step-link"
                                    >
                                        <div class="step-number">{{ index + 1 }}</div>
                                        <div class="step-name">{{ step.name }}</div>
                                    </a>
                                </div>
                            </div>
                        </template>
                    </AnimationWrapper>
                </template>
            </div>
        </div>
    </SectionBase>
</template>

<script>
import SectionBase from "./SectionBase.vue";
import AnimationWrapper from "../../../common/components/AnimationWrapper.vue";
import {hexToHSL, resolveColor} from "../../../common/helpers.js";
import {PageTypesEnum} from "../../../common/enums.js";
import {mapGetters} from "vuex";

export default {
    name: "Process",
    components: {SectionBase, AnimationWrapper },
    props: ['section'],
    data() {
        return {
            stuck: false,
            hideStuckVersion: false,
            showMobileProcessSteps: false
        }
    },
    created() {
        window.addEventListener('scroll', this.handleScroll);
    },
    unmounted() {
        this.viewData.stickedHeader = false;
        window.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
        scrollToItem(e, href) {
            e.preventDefault();
            if(this.stuck) {
                this.showMobileProcessSteps = false;
            }

            const target = document.querySelector(`[data-section-index="${href}"]`);
            if (!target) return;

            const scrollDistanceFixer = this.stuck ? 108 : this.$refs.processBar.offsetHeight + 108; // 68
            const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - scrollDistanceFixer;

            window.scrollTo({
                top: targetPosition,
                left: 0,
                behavior: 'smooth'
            });
        },
        handleScroll() {
            const processBar = this.$refs.processBar;
            const processStepsContainer = this.$refs.processSteps;
            if (!processBar || !processStepsContainer) return;

            const processSteps = processStepsContainer.querySelectorAll('.process-step');
            const stickyOffset = processBar.offsetHeight || 0;
            const lastLinkedStep = [...this.processItems]
                .reverse()
                .find((step) => step.sectionIndex !== null && step.sectionIndex !== undefined);
            const lastStepElement = lastLinkedStep
                ? document.querySelector(`[data-section-index="section-${lastLinkedStep.sectionIndex}"]`)
                : null;

            // Calculate sticky start and end positions
            const startY = processBar.parentNode.getBoundingClientRect().top;
            const endY = lastStepElement
                ? lastStepElement.getBoundingClientRect().top + lastStepElement.getBoundingClientRect().height - 70
                : 0;

            // Handle when the user scrolls back to the top
            if (startY >= 0) {
                this.stuck = false;
                processSteps.forEach((step) => step.classList.remove('checked'));
                this.hideStuckVersion = false;
                this.showMobileProcessSteps = false;
                this.viewData.stickedHeader = false;
                return;
            } else if (endY <= 0) {
                this.hideStuckVersion = true;
                this.showMobileProcessSteps = false;
                return;
            } else if (startY <= 0) {
                this.hideStuckVersion = false;
                this.stuck = true;
                this.viewData.stickedHeader = true;

                processSteps.forEach((step) => {
                    const anchorHref = step.querySelector('.step-link')?.getAttribute('href');
                    const anchorId = anchorHref ? anchorHref.split('#')[1] : null;
                    const stepAnchor = anchorId
                        ? document.querySelector(`[data-section-index="${anchorId}"]`)
                        : null;
                    const stepY = stepAnchor ? stepAnchor.getBoundingClientRect().top : window.innerHeight + 1;
                    if (stepY <= stickyOffset) {
                        step.classList.add('checked');
                    } else {
                        step.classList.remove('checked');
                    }
                });

                const checkedSteps = Array.from(this.$refs.processBar.querySelectorAll('.process-step.checked'));
                if (checkedSteps.length > 0) {
                    this.$refs.processBar.querySelector('.process-step-display').innerHTML = checkedSteps.pop().innerHTML;
                }
            }
        },

        toggleProcessSteps() {
            this.showMobileProcessSteps = !this.showMobileProcessSteps
        },
    },
    computed: {
        ...mapGetters("view", ["viewData"]),
        processItems() {
            return this.section.processItems || [];
        },
        project() {
            return this.viewData.project || this.$store.state.project;
        },
        portfolio() {
            return this.viewData.portfolio || this.$store.state.portfolio;
        },
        colors() {
            return { '--checkedStepColor': resolveColor(this.project.designSettings.colorStyles.navigationHover, this.project.designSettings.colorPalette)};
        },
        setStartSection() {
            if (this.processItems.length) {
                return this.processItems.find(e => e.sectionIndex) ? this.processItems.find(e => e.sectionIndex).sectionIndex : 1;
            }
        },
        setEndSection() {
            if (this.processItems.length) {
                const reversed = JSON.parse(JSON.stringify(this.processItems)).reverse();
                return reversed.find(e => e.sectionIndex) ? reversed.find(e => e.sectionIndex).sectionIndex : null;
            }
        },
        portfolioIsDark() {
            return hexToHSL(resolveColor(this.colorStyles.background, this.colorPalette)).l < 0.2;
        },
        colorStyles() {
            return this.isCaseStudy && !this.usingGlobalColorStyles ?
                this.project.designSettings.colorStyles :
                this.portfolio.designSettings.colorStyles;
        },
        colorPalette() {
            return this.isCaseStudy && !this.usingGlobalColorStyles ?
            this.project.designSettings.colorPalette :
            this.portfolio.designSettings.colorPalette;
        },
        isCaseStudy() {
            return this.$store.state.project && this.$store.state.project.type === PageTypesEnum.CASE_STUDY;
        },
        usingGlobalColorStyles() {
            return this.project.type === PageTypesEnum.CASE_STUDY && this.project.designSettings.globalColorStyles;
        },
    }
}
</script>
